/*
* Broadcast Theme
*
* Use this file to add custom Javascript to Broadcast.  Keeping your custom
* Javascript in this fill will make it easier to update Broadcast. In order
* to use this file you will need to open layout/theme.liquid and uncomment
* the custom.js script import line near the bottom of the file.
*/


(function() {
  // Add custom code below this line

  /**
   * Justuno pushdown bar sync
   * This watches for Justuno's bar, keeps --announcement-height in sync with
   * its live height, and dispatches `theme:pushdown:settled` so we can re-measure
   * the header offset and sticky heights (see header-component.updateHeaderOffset
   * and setVars in theme.dev.js).
   */
  (function () {
    const PUSHDOWN_SELECTOR = '[id^="_jst-iframe"]';
    const PINNED_CLASS = 'has-pinned-announcement';
    const FADE_CLASS = 'js-jst-pushdown';

    let observedElement = null; // topmost container under <body> - measured for height
    let matchedIframe = null; // the actual [id^="_jst-iframe"] element - faded in
    let resizeObserver = null;
    let hasFadedIn = false;

    const setAnnouncementHeight = (height) => {
      document.documentElement.style.setProperty('--announcement-height', `${height}px`);
    };

    const setPinned = (isPinned) => {
      document.body.classList.toggle(PINNED_CLASS, isPinned);
    };

    const notifySettled = () => {
      document.dispatchEvent(new CustomEvent('theme:pushdown:settled', {bubbles: true}));
    };

    const fadeIn = (el) => {
      el.style.opacity = '1';
      // Detach from CSS fallback rule now that JS has revealed it
      el.classList.remove(FADE_CLASS);
    };

    const onSettle = () => {
      if (!observedElement || !observedElement.isConnected) return;

      const height = observedElement.offsetHeight;

      // Reveal the first time the bar actually measures a height
      if (!hasFadedIn && height > 0 && matchedIframe) {
        hasFadedIn = true;
        fadeIn(matchedIframe);
      }

      setAnnouncementHeight(height);
      notifySettled();
    };

    const handleResize = window.theme?.debounce ? window.theme.debounce(onSettle, 100) : onSettle;

    const observe = (topLevelEl, iframeEl) => {
      if (observedElement === topLevelEl) return;

      resizeObserver?.disconnect();
      observedElement = topLevelEl;
      matchedIframe = iframeEl;
      hasFadedIn = false;
      iframeEl.classList.add(FADE_CLASS);

      const position = getComputedStyle(iframeEl).position;
      setPinned(position === 'sticky' || position === 'fixed');

      if ('ResizeObserver' in window) {
        resizeObserver = new ResizeObserver(handleResize);
        resizeObserver.observe(topLevelEl);
      }

      // Measure immediately too, in case the element is already at its
      // settled size by the time we start observing it.
      onSettle();
    };

    // Observe whichever container actually occupies pushdown space
    const getTopLevelContainer = (el) => {
      let node = el;
      while (node.parentElement && node.parentElement !== document.body) {
        node = node.parentElement;
      }
      return node;
    };

    // Other Justuno campaigns (fullscreen popups) share the same
    // [id^="_jst-iframe"] naming, so match on more than just the ID -
    // only a bar Justuno has itself marked position: sticky is the
    // pushdown bar we want to reserve space for
    const findPushdownBar = () => {
      const candidates = document.body.querySelectorAll(PUSHDOWN_SELECTOR);
      for (const candidate of candidates) {
        if (getComputedStyle(candidate).position === 'sticky') {
          return {topLevel: getTopLevelContainer(candidate), iframe: candidate};
        }
      }
      return null;
    };

    const checkForPushdownBar = () => {
      const bar = findPushdownBar();

      if (bar) {
        observe(bar.topLevel, bar.iframe);
      } else if (observedElement) {
        // Reset so the layout re-settles without it.
        resizeObserver?.disconnect();
        resizeObserver = null;
        observedElement = null;
        matchedIframe = null;
        setPinned(false);
        setAnnouncementHeight(0);
        notifySettled();
      }
    };

    const init = () => {
      checkForPushdownBar();

      // Watch for Justuno inserting/removing its bar anywhere under <body>
      const bodyObserver = new MutationObserver(checkForPushdownBar);
      bodyObserver.observe(document.body, {childList: true, subtree: true});
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  })();

  /**
   * Justuno header inline-style override guard
   */
  (function () {
    const HEADER_SELECTOR = 'header-component';

    const stripInlineTop = (el) => {
      if (el.style.top) el.style.removeProperty('top');
    };

    const watch = () => {
      const header = document.querySelector(HEADER_SELECTOR);
      if (!header) return;

      stripInlineTop(header);

      const styleObserver = new MutationObserver(() => stripInlineTop(header));
      styleObserver.observe(header, {attributes: true, attributeFilter: ['style']});
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', watch);
    } else {
      watch();
    }
  })();


  // ^^ Keep your scripts inside this IIFE function call to
  // avoid leaking your variables into the global scope.
})();
